import { useCallback } from "react";

/** Returns a Clerk session token, or null when unauthenticated / in preview. */
export type GetProToken = () => Promise<string | null>;

interface ClerkGlobal {
  session?: { getToken: () => Promise<string | null> } | null;
}

/**
 * Token resolver for the data layer. It calls EXACTLY ONE hook (`useCallback`)
 * regardless of environment, so the Rules of Hooks hold and Fast Refresh stays
 * stable — unlike a branch that swaps between a `useAuth`-based impl and a no-op
 * impl (those have different internal hook shapes, which trips React's
 * hook-order check).
 *
 * It reads Clerk off the `window.Clerk` global (which `ClerkProvider` populates
 * when mounted) rather than the `useAuth()` hook, so it works identically
 * whether or not a provider is present — preview has none, prod does, and the
 * same single `useCallback` runs either way. The token is only ever consumed on
 * the prod data path (the preview loaders short-circuit to fixtures first).
 */
export function useProToken(): GetProToken {
  return useCallback(async () => {
    const clerk = (globalThis as { Clerk?: ClerkGlobal }).Clerk;
    if (!clerk?.session) return null;
    try {
      return await clerk.session.getToken();
    } catch {
      return null;
    }
  }, []);
}
